import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
  AUDIT_CREATE,
  AUDIT_CREATE_COMPLETED,
  AUDIT_CREATE_VIEW,
  AUDIT_EDIT_MODE,
  AUDIT_FIND,
  AUDIT_FIND_ALL,
  AUDIT_FIND_ALL_SUCCESS,
  AUDIT_FIND_SUCCESS,
  AUDIT_REMOVE,
  AUDIT_REMOVE_COMPLETED,
  AUDIT_RESET_ERROR,
  AUDIT_UPDATE,
  AUDIT_UPDATE_COMPLETED,
  AUDIT_UPDATE_ID,
  AUDIT_UPDATE_LOADING,
  AUDIT_UPDATE_VIEW,
} from '@store/actions';
import { Auditoria, AuditoriaForm } from '@core/models';
import { AccountService, AuthService, AuditService } from '@core/services';
import { selectAccountId, selectAuditForm } from '@store/selectors';
import { RolModuleEnum } from '@core/enums';

@Injectable()
export class AuditEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private auditService: AuditService,
    private accountService: AccountService,
    private authService: AuthService,
  ) {}

   createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_CREATE_VIEW),
      mergeMap(() => {
        return [
          AUDIT_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_UPDATE_VIEW),
      mergeMap(({ auditId }) => {
        return [
          AUDIT_UPDATE_ID({ auditId }),
          AUDIT_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(AUDIT_UPDATE_LOADING({  loading: true }));

        return this.auditService.find(id).pipe(
          mergeMap(({ data }) => {
            return [
              AUDIT_UPDATE_LOADING({ loading: false }),
              AUDIT_FIND_SUCCESS({ audit: data }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUDIT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_FIND_ALL),
      filter(() => this.authService.hasRole(RolModuleEnum.Role)),
      mergeMap(() => {
        this.store.dispatch(AUDIT_UPDATE_LOADING({  loading: true }));

        return this.auditService.findAll().pipe(
          mergeMap(({ data }) => {
            const auditList = data as Auditoria[];

            return [
              AUDIT_FIND_ALL_SUCCESS({ auditList }),
              AUDIT_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUDIT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_CREATE),
      withLatestFrom(
        this.store.select(selectAuditForm),
      ),
      filter(([_, attr ]) =>  !!attr),
      map(([_, attr]) => ({ attr: attr as AuditoriaForm })),
      mergeMap(({ attr }) => {
        this.store.dispatch(AUDIT_UPDATE_LOADING({  loading: true }));

        return this.auditService.create(attr).pipe(
          mergeMap(() => {
            return [
              // NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Audit successfully created' } }),
              AUDIT_UPDATE_LOADING({ loading: false }),
              AUDIT_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUDIT_RESET_ERROR(),
                  AUDIT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_CREATE_COMPLETED),
      tap(() => {
        this.auditService.creationCompleted();
        this.accountService.closeDialog();
      })
    ),
    { dispatch: false }
  )

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_UPDATE),
      withLatestFrom(
        this.store.select(selectAccountId),
        this.store.select(selectAuditForm),
      ),
      filter(([_, id, attrs ]) =>  !!id && !!attrs),
      map(([_, id, attrs]) => ({ id: id as string, attrs: attrs as AuditoriaForm })),
      mergeMap(({ id, attrs }) => {
        this.store.dispatch(AUDIT_UPDATE_LOADING({  loading: true }));

        return this.auditService.update(id, attrs).pipe(
          mergeMap(() => {
            return [
              // NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Audit successfully updated' } }),
              AUDIT_UPDATE_LOADING({ loading: false }),
              AUDIT_UPDATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUDIT_RESET_ERROR(),
                  AUDIT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_UPDATE_COMPLETED),
      tap(() => {
        this.auditService.updateCompleted();
        this.accountService.closeDialog();
      })
    ),
    { dispatch: false }
  )

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_REMOVE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.auditService.remove(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Audit successfully removed' } }),
              AUDIT_REMOVE_COMPLETED(),
              AUDIT_FIND_ALL(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUDIT_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  removeComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUDIT_REMOVE_COMPLETED),
      tap(() => {})
    ),
    { dispatch: false }
  )
}
